import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  canViewFinancial, canCreateAsset, canEditMaintenance, canIssueSpare,
  canTransferAsset, canWriteOffAsset,
  hasCapability, isAdminRole,
  formatINR, formatDate, ROLE_LABELS,
} from "@/lib/permissions";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SpareIssuePanel from "@/components/SpareIssuePanel";
import AssetAttachments from "@/components/AssetAttachments";
import { TransferDialog, WriteOffDialog } from "@/components/AssetActionDialogs";
import { DeployAssetDialog, describeDeployment } from "@/components/DeployAssetDialog";
import { ArrowRightLeft, XCircle, FileDown, Send, Trash2, History } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Edit, QrCode, Wrench, AlertTriangle, Sparkles,
  MapPin, Calendar, Building2, Hash, Package, Download, Lock, Loader2, Pencil, Check, X as XIcon,
} from "lucide-react";

const STATUS_STYLES = {
  active: "bg-green-50 text-green-700 border-green-200",
  under_maintenance: "bg-amber-50 text-amber-700 border-amber-200",
  retired: "bg-slate-100 text-slate-600 border-slate-200",
  disposed: "bg-red-50 text-red-700 border-red-200",
};

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [logs, setLogs] = useState([]);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const [maintOpen, setMaintOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [deployOpen, setDeployOpen] = useState(false);
  const [deployHistory, setDeployHistory] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [renaming, setRenaming] = useState(false);
  const isAdmin = isAdminRole(user?.role);

  const saveName = async () => {
    const next = (nameDraft || "").trim();
    if (!next || next === asset?.name) { setEditingName(false); return; }
    setRenaming(true);
    try {
      const { data } = await api.patch(`/assets/${asset.id}`, { name: next });
      setAsset(data);
      toast.success("Asset name updated");
      setEditingName(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to rename");
    } finally { setRenaming(false); }
  };

  const downloadWriteOffCert = async (assetId, assetCode) => {
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${API_BASE}/assets/${assetId}/write-off/certificate.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `WriteOff_${assetCode || assetId}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
  };
  const showFinancial = canViewFinancial(user?.role);
  const canEdit = canCreateAsset(user?.role);
  const canLog = canEditMaintenance(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [a, l, q, d] = await Promise.all([
        api.get(`/assets/${id}`),
        api.get(`/assets/${id}/maintenance`),
        api.get(`/assets/${id}/qr`),
        api.get(`/assets/${id}/deployments`),
      ]);
      setAsset(a.data);
      setLogs(l.data);
      setQr(q.data);
      setDeployHistory(d.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load asset");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const runAI = async () => {
    setAiLoading(true);
    setAiResult("");
    try {
      const { data } = await api.post("/ai/maintenance-suggestion", { asset_id: id });
      setAiResult(data.suggestion);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI suggestion failed");
    } finally { setAiLoading(false); }
  };

  if (loading || !asset) {
    return <div className="text-slate-500">Loading asset details...</div>;
  }

  return (
    <div className="space-y-6" data-testid="asset-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button
          onClick={() => navigate("/assets")}
          className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"
          data-testid="back-to-registry"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Registry
        </button>
        <div className="flex items-center gap-2">
          {asset.status !== "disposed" && hasCapability(user, "deploy_asset") && (
            <Button variant="outline" onClick={() => setDeployOpen(true)} data-testid="deploy-asset-btn" className="border-[#7C1D3A] text-[#7C1D3A] hover:bg-[#fcf0f3]">
              <Send className="w-4 h-4 mr-2" /> Deploy
            </Button>
          )}
          {asset.status !== "disposed" && canTransferAsset(user?.role) && (
            <Button variant="outline" onClick={() => setTransferOpen(true)} data-testid="transfer-asset-btn">
              <ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer
            </Button>
          )}
          {asset.status !== "disposed" && canWriteOffAsset(user?.role) && (
            <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => setWriteOffOpen(true)} data-testid="writeoff-asset-btn">
              <XCircle className="w-4 h-4 mr-2" /> Write-off
            </Button>
          )}
          {asset.writeoff_id && (
            <Button variant="outline" onClick={() => downloadWriteOffCert(asset.id, asset.asset_id)} data-testid="download-writeoff-btn">
              <FileDown className="w-4 h-4 mr-2" /> Write-off Cert
            </Button>
          )}
          {asset.status !== "disposed" && (
            <Button
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => navigate(`/breakdown-report?asset=${asset.id}`)}
              data-testid="report-breakdown-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" /> Report Breakdown
            </Button>
          )}
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => navigate(`/assets/${id}/edit`)}
              data-testid="edit-asset-btn"
            >
              <Edit className="w-4 h-4 mr-2" /> Edit
            </Button>
          )}
        </div>
      </div>

      {transferOpen && <TransferDialog open={transferOpen} onOpenChange={setTransferOpen} asset={asset} onDone={() => { setTransferOpen(false); load(); }} />}
      {writeOffOpen && <WriteOffDialog open={writeOffOpen} onOpenChange={setWriteOffOpen} asset={asset} onDone={() => { setWriteOffOpen(false); load(); }} />}
      {deployOpen && <DeployAssetDialog open={deployOpen} onOpenChange={setDeployOpen} asset={asset} onDone={() => { setDeployOpen(false); load(); }} />}

      {/* Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6 flex-wrap">
            <div className="w-24 h-24 rounded-lg bg-[#fcf0f3] border border-[#f5c4d3] flex items-center justify-center shrink-0 overflow-hidden">
              {asset.image_url ? (
                <img src={asset.image_url} alt={asset.name} className="w-full h-full object-cover" />
              ) : (
                <Package className="w-10 h-10 text-[#7C1D3A]" />
              )}
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded" data-testid="asset-code">
                  {asset.asset_id}
                </span>
                <Badge className={`${STATUS_STYLES[asset.status]} border capitalize`}>
                  {asset.status?.replace("_", " ")}
                </Badge>
                <Badge variant="outline">{asset.category}</Badge>
              </div>
              {editingName ? (
                <div className="flex items-center gap-2 mt-2" data-testid="asset-name-edit">
                  <Input
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                    className="text-2xl font-heading font-bold text-slate-900 h-11 max-w-lg"
                    data-testid="asset-name-input"
                  />
                  <Button onClick={saveName} disabled={renaming} className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid="asset-name-save">
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" onClick={() => { setEditingName(false); setNameDraft(asset.name); }} className="h-9">
                    <XIcon className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-2">
                  <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="asset-name">
                    {asset.name}
                  </h1>
                  {isAdmin && (
                    <button
                      onClick={() => { setNameDraft(asset.name || ""); setEditingName(true); }}
                      className="text-slate-400 hover:text-[#7C1D3A] p-1.5 rounded hover:bg-[#fcf0f3] transition"
                      title="Rename (admin only)"
                      data-testid="asset-name-edit-btn"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
              <p className="text-slate-500 text-sm mt-1">
                {asset.manufacturer} · {asset.model_number || "—"} · SN {asset.serial_number || "—"}
              </p>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <div className="flex items-center gap-1.5 text-slate-600">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />{asset.location || "—"}
                </div>
                <div className="flex items-center gap-1.5 text-slate-600" data-testid="asset-deployment">
                  <Send className="w-3.5 h-3.5 text-slate-400" />{describeDeployment(asset.deployment) || <span className="text-slate-400">Not deployed</span>}
                </div>
                <div className="flex items-center gap-1.5 text-slate-600">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />Purchased {formatDate(asset.purchase_date)}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deployment history — visible to all who can view assets */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <History className="w-4 h-4" /> Deployment History
            {asset.deployment && hasCapability(user, "deploy_asset") && (
              <button
                onClick={async () => {
                  if (!window.confirm("Clear the current deployment? History will be preserved.")) return;
                  try {
                    await api.delete(`/assets/${asset.id}/deployment`);
                    toast.success("Deployment cleared");
                    load();
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail) || "Failed to clear");
                  }
                }}
                className="ml-auto text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                data-testid="clear-deployment-btn"
              >
                <Trash2 className="w-3.5 h-3.5" /> Clear current
              </button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {deployHistory.length === 0 ? (
            <div className="text-sm text-slate-500 py-2" data-testid="deploy-history-empty">
              No deployments yet. Use the <b>Deploy</b> button to assign this asset to a Line, Person or Department within its location.
            </div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="deploy-history-list">
              {deployHistory.map((h) => {
                const fromLabel = h.from_deployment ? describeDeployment(h.from_deployment) : <span className="text-slate-400">not deployed</span>;
                const toLabel = h.to_deployment ? describeDeployment(h.to_deployment) : <span className="text-red-600">Cleared</span>;
                return (
                  <div key={h.id} className="py-2 flex items-start gap-3 text-sm">
                    <div className="text-xs text-slate-500 min-w-[95px]">{formatDate(h.effective_date)}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-slate-600">{fromLabel}</span>
                        <ArrowRightLeft className="w-3 h-3 text-slate-400" />
                        <span className="font-medium">{toLabel}</span>
                      </div>
                      {h.notes && <div className="text-xs text-slate-500 mt-0.5">{h.notes}</div>}
                      <div className="text-xs text-slate-400 mt-0.5">by {h.performed_by_name}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Spare / Jig linkage panel — always shown for spares, and shown for machines only if there's history */}
      <SpareIssuePanel asset={asset} canIssueSpare={canIssueSpare(user?.role)} onChanged={load} />

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full sm:w-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="financial" data-testid="tab-financial">
            {showFinancial ? "Financial" : (<span className="flex items-center gap-1"><Lock className="w-3 h-3" />Financial</span>)}
          </TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">Maintenance</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="qr" data-testid="tab-qr">QR Code</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">AI Assistant</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Asset Overview</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
              <Row label="Description" value={asset.description || "—"} />
              <Row label="Serial Number" value={asset.serial_number || "—"} mono />
              <Row label="Brand" value={asset.brand || "—"} />
              <Row label="Invoicing Party" value={asset.manufacturer || "—"} />
              <Row label="Model Number" value={asset.model_number || "—"} mono />
              <Row label="Location" value={asset.location || "—"} />
              <Row label="Assigned To" value={asset.assigned_to || "Unassigned"} />
              <Row label="Invoice Number" value={asset.invoice_number || "—"} mono />
              <Row label="Origin Currency" value={asset.origin_currency || "INR"} />
              <Row label="Warranty Expires" value={formatDate(asset.warranty_expires)} />
              <Row label="AMC Vendor" value={asset.amc_vendor || "—"} />
              <Row label="AMC End Date" value={formatDate(asset.amc_end_date)} />
              <Row label="Created" value={formatDate(asset.created_at)} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial */}
        <TabsContent value="financial">
          {!showFinancial ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Lock className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <div className="font-heading text-lg font-semibold text-slate-900">Restricted Access</div>
                <p className="text-sm text-slate-500 mt-1">
                  Financial data is visible only to Admin, Manager, Accounts Manager and Accounts Assistant roles.
                  <br />You are logged in as <b>{ROLE_LABELS[user?.role]}</b>.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-base">Financial & Depreciation</CardTitle>
                <Badge variant="outline" className="text-[#7C1D3A] border-[#f5c4d3]">Restricted</Badge>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
                <StatBox label="Purchase Value" value={formatINR(asset.purchase_value)} />
                <StatBox label="Current Book Value" value={formatINR(asset.current_value)} highlight />
                <StatBox label="Accumulated Depreciation" value={formatINR(asset.accumulated_depreciation)} />
                <StatBox label="Method" value={asset.depreciation_method} />
                <StatBox label="Useful Life" value={`${asset.useful_life_years} years`} />
                <StatBox label="WDV Rate" value={`${asset.depreciation_rate}%`} />
                <StatBox label="Salvage Value" value={formatINR(asset.salvage_value)} />
                <StatBox label="Annual Depreciation" value={formatINR(asset.annual_depreciation)} />
                <StatBox label="Years Elapsed" value={`${asset.years_elapsed} yr`} />
                <StatBox label="AMC Annual Cost" value={formatINR(asset.amc_cost)} />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Maintenance */}
        <TabsContent value="maintenance">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-base">Maintenance & Repair Log</CardTitle>
              {canLog && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/assets/${id}/schedule/regenerate`);
                        toast.success(`Regenerated ${data.generated} scheduled entries`);
                        load();
                      } catch (e) {
                        toast.error(formatApiError(e.response?.data?.detail) || "Failed to regenerate");
                      }
                    }}
                    data-testid="regenerate-schedule-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" /> Regenerate Schedule
                  </Button>
                  <Dialog open={maintOpen} onOpenChange={setMaintOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="add-maintenance-btn">
                        <Wrench className="w-4 h-4 mr-2" /> Log Entry
                      </Button>
                    </DialogTrigger>
                    <MaintenanceForm
                      assetId={id}
                      onDone={() => { setMaintOpen(false); load(); }}
                    />
                  </Dialog>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {logs.length === 0 ? (
                <div className="p-12 text-center text-slate-500 text-sm">
                  <Wrench className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  No maintenance logs recorded yet.
                </div>
              ) : (
                <div className="divide-y divide-slate-100" data-testid="maintenance-logs">
                  {logs.map((log) => (
                    <div key={log.id} className={`p-5 hover:bg-slate-50 ${log.status === "scheduled" ? "bg-slate-50/50" : ""}`}>
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                            log.type === "breakdown" ? "bg-red-50 text-red-600"
                            : log.status === "scheduled" ? "bg-slate-100 text-slate-500"
                            : "bg-green-50 text-green-600"
                          }`}>
                            {log.type === "breakdown" ? <AlertTriangle className="w-4 h-4" /> : <Wrench className="w-4 h-4" />}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900 text-sm capitalize flex items-center gap-2">
                              {log.type}
                              {log.auto_generated && (
                                <Badge variant="outline" className="border-slate-300 text-slate-500 text-[10px] px-1.5 py-0">
                                  <Sparkles className="w-2.5 h-2.5 mr-0.5" /> Auto
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 font-mono">{formatDate(log.date)} · By {log.created_by_name || log.performed_by}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {showFinancial && log.cost != null && (
                            <span className="font-mono text-sm text-slate-700">{formatINR(log.cost)}</span>
                          )}
                          <Badge variant="outline" className="capitalize">{log.status?.replace("_", " ")}</Badge>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-slate-700">{log.description}</p>
                      {log.resolution && (
                        <div className="mt-2 pl-4 border-l-2 border-[#f5c4d3] text-xs text-slate-600">
                          <span className="font-semibold text-slate-500">Resolution: </span>{log.resolution}
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500 items-center">
                        {log.assigned_to_name && (
                          <span className="inline-flex items-center gap-1">
                            Assigned to <b className="text-slate-700">{log.assigned_to_name}</b>
                          </span>
                        )}
                        {log.due_date && (() => {
                          const days = Math.ceil((new Date(log.due_date) - new Date()) / 86400000);
                          const isDone = log.status === "completed";
                          const cls = isDone ? "bg-slate-100 text-slate-600 border-slate-200"
                            : days < 0 ? "bg-red-50 text-red-700 border-red-200"
                            : days <= 2 ? "bg-amber-50 text-amber-800 border-amber-200"
                            : "bg-emerald-50 text-emerald-800 border-emerald-200";
                          const label = isDone ? `Due was ${formatDate(log.due_date)}`
                            : days < 0 ? `Overdue by ${-days}d · due ${formatDate(log.due_date)}`
                            : days === 0 ? `Due today`
                            : `Due in ${days}d · ${formatDate(log.due_date)}`;
                          return <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] ${cls}`}>{label}</span>;
                        })()}
                        {log.root_cause_code && (
                          <span className="inline-block px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-[11px] text-slate-700">
                            Cause · {log.root_cause_code}
                          </span>
                        )}
                        {log.next_due_date && <span>Next Due: <b className="text-slate-700">{formatDate(log.next_due_date)}</b></span>}
                        {log.downtime_hours > 0 && <span>Downtime: <b className="text-slate-700">{log.downtime_hours} hr</b></span>}
                      </div>
                      {log.photos && log.photos.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {log.photos.map((p, i) => (
                            <a key={i} href={p} target="_blank" rel="noreferrer" className="block w-16 h-16 rounded-md overflow-hidden border border-slate-200 hover:border-[#7C1D3A]">
                              <img src={p} alt={`photo ${i + 1}`} className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base">Documents &amp; Attachments</CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Invoices, warranty cards, user manuals, insurance policies, calibration certificates — one place for auditors.
              </p>
            </CardHeader>
            <CardContent>
              <AssetAttachments assetId={id} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* QR */}
        <TabsContent value="qr">
          <Card>
            <CardHeader><CardTitle className="font-heading text-base">QR Code Label</CardTitle></CardHeader>
            <CardContent className="p-8 text-center">
              <div className="inline-block bg-white border-2 border-slate-200 rounded-xl p-6 mb-4">
                {qr?.qr_png_base64 && (
                  <img src={qr.qr_png_base64} alt="QR" className="w-56 h-56 mx-auto" data-testid="qr-image" />
                )}
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="font-heading font-bold text-sm text-slate-900">{asset.name}</div>
                  <div className="font-mono text-xs text-slate-500 mt-1">{asset.asset_id}</div>
                </div>
              </div>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                Scan with any smartphone camera to open this asset's detail page instantly. Print and affix to physical asset.
              </p>
              <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                <a
                  href={qr?.qr_png_base64}
                  download={`${asset.asset_id}.png`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#7C1D3A] hover:underline"
                  data-testid="download-qr"
                >
                  <Download className="w-4 h-4" /> Download PNG
                </a>
                <span className="text-slate-300">·</span>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("assetiq_token");
                      const { API_BASE } = await import("@/lib/api");
                      const res = await fetch(`${API_BASE}/assets/labels/pdf`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ asset_ids: [id] }),
                      });
                      if (!res.ok) throw new Error("Failed");
                      const blob = await res.blob();
                      const link = document.createElement("a");
                      link.href = URL.createObjectURL(blob);
                      link.download = `${asset.asset_id}-label.pdf`;
                      link.click();
                      URL.revokeObjectURL(link.href);
                      toast.success("Label PDF downloaded");
                    } catch { toast.error("Failed to generate label PDF"); }
                  }}
                  className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#7C1D3A]"
                  data-testid="download-label-pdf"
                >
                  <QrCode className="w-4 h-4" /> Print Label (A4 sheet)
                </button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI */}
        <TabsContent value="ai">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#7C1D3A]" /> AI Predictive Maintenance
              </CardTitle>
              <Button
                onClick={runAI}
                disabled={aiLoading}
                data-testid="generate-ai-btn"
                className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
              >
                {aiLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generate Recommendation</>
                )}
              </Button>
            </CardHeader>
            <CardContent className="p-6">
              {!aiResult && !aiLoading && (
                <div className="text-center text-slate-500 text-sm py-8">
                  <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  Get AI-powered maintenance recommendations based on this asset's history and category.
                </div>
              )}
              {aiResult && (
                <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap" data-testid="ai-result">
                  {aiResult}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs font-semibold tracking-wider uppercase text-slate-500">{label}</div>
      <div className={`mt-1 text-sm text-slate-900 whitespace-pre-line ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function StatBox({ label, value, highlight }) {
  return (
    <div className={`p-4 rounded-lg border ${highlight ? "bg-[#fcf0f3] border-[#f5c4d3]" : "bg-slate-50 border-slate-200"}`}>
      <div className="text-xs font-semibold tracking-wider uppercase text-slate-500">{label}</div>
      <div className={`mt-2 font-heading text-xl font-bold ${highlight ? "text-[#7C1D3A]" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function MaintenanceForm({ assetId, onDone }) {
  const { user } = useAuth();
  // Feb 2026 — direct breakdown logging is limited to admin/manager/maint_mgr.
  // Everyone else must go through the Report Breakdown workflow.
  const canDirectBreakdown = ["admin", "admin_addl", "manager", "admin_manager", "maintenance_manager"].includes(user?.role);
  const [type, setType] = useState("preventive");
  const [description, setDescription] = useState("");
  const [performedBy, setPerformedBy] = useState("");
  const [cost, setCost] = useState("0");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [nextDue, setNextDue] = useState("");
  const [downtime, setDowntime] = useState("0");
  const [resolution, setResolution] = useState("");
  const [status, setStatus] = useState("completed");
  const [rootCause, setRootCause] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [photos, setPhotos] = useState([]);   // data URLs
  const [technicians, setTechnicians] = useState([]);
  const [consumablesMaster, setConsumablesMaster] = useState([]);
  const [consumablesUsed, setConsumablesUsed] = useState([]); // [{id, consumable_id, qty, note}]
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/users/technicians").then(({ data }) => setTechnicians(data || [])).catch(() => {});
    api.get("/consumables").then(({ data }) => setConsumablesMaster(data || [])).catch(() => {});
  }, []);

  const addConsumable = () =>
    setConsumablesUsed((rows) => [...rows, { id: crypto.randomUUID(), consumable_id: "", qty: 1, note: "" }]);
  const updateConsumable = (id, field, value) =>
    setConsumablesUsed((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  const removeConsumable = (id) =>
    setConsumablesUsed((rows) => rows.filter((r) => r.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/assets/${assetId}/maintenance`, {
        type, description, performed_by: performedBy || null,
        cost: parseFloat(cost) || 0,
        date, next_due_date: nextDue || null,
        downtime_hours: parseFloat(downtime) || 0,
        resolution: resolution || null, status,
        root_cause_code: type === "breakdown" ? (rootCause || null) : null,
        assigned_to: assignedTo || null,
        due_date: dueDate || null,
        photos: photos.length ? photos : null,
        consumables_used: consumablesUsed
          .filter((r) => r.consumable_id && Number(r.qty) > 0)
          .map((r) => ({ consumable_id: r.consumable_id, qty: Number(r.qty), note: r.note || null })),
      });
      toast.success("Maintenance log added");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add log");
    } finally { setSubmitting(false); }
  };

  return (
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading">Log Maintenance / Repair</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="maint-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="preventive">Preventive</SelectItem>
                {canDirectBreakdown && <SelectItem value="breakdown">Breakdown</SelectItem>}
              </SelectContent>
            </Select>
            {!canDirectBreakdown && (
              <p className="text-[10px] text-slate-500 mt-1">
                To log a breakdown, use <b>Report Breakdown</b> at the top of this page.
              </p>
            )}
          </div>
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Textarea required value={description} onChange={(e) => setDescription(e.target.value)} data-testid="maint-description" />
        </div>
        {type === "breakdown" && (
          <div>
            <Label>Root cause code</Label>
            <Select value={rootCause} onValueChange={setRootCause}>
              <SelectTrigger data-testid="maint-root-cause"><SelectValue placeholder="— pick a category —" /></SelectTrigger>
              <SelectContent>
                {["Electrical","Mechanical","Hydraulic/Pneumatic","Operator Error","Wear-and-Tear","Consumable","External Power","Software/Control","Other"].map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1">Feeds the Pareto chart on Reports · Maintenance.</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Assign to technician</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger data-testid="maint-assigned"><SelectValue placeholder="— unassigned —" /></SelectTrigger>
              <SelectContent>
                {technicians.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No maintenance staff configured</div>}
                {technicians.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} · <span className="text-slate-500 text-xs">{t.role.replace("_", " ")}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Due by</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="maint-due-date" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date</Label><Input type="date" required value={date} onChange={(e) => setDate(e.target.value)} data-testid="maint-date" /></div>
          <div><Label>Next Due</Label><Input type="date" value={nextDue} onChange={(e) => setNextDue(e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Performed By</Label><Input value={performedBy} onChange={(e) => setPerformedBy(e.target.value)} /></div>
          <div><Label>Downtime (hrs)</Label><Input type="number" step="0.5" value={downtime} onChange={(e) => setDowntime(e.target.value)} /></div>
        </div>
        <div><Label>Cost (₹)</Label><Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="maint-cost" /></div>
        <div><Label>Resolution / Notes</Label><Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} /></div>
        <div>
          <Label>Photos <span className="text-slate-400 text-[11px] font-normal">(camera-friendly, jpeg/png · max 4MB total)</span></Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {photos.map((p, idx) => (
              <div key={idx} className="relative w-20 h-20 rounded-md overflow-hidden border border-slate-200">
                <img src={p} alt={`photo ${idx + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setPhotos((ps) => ps.filter((_, i) => i !== idx))}
                  className="absolute top-0 right-0 bg-black/60 text-white text-xs w-5 h-5 flex items-center justify-center rounded-bl"
                  aria-label="Remove photo"
                >×</button>
              </div>
            ))}
            <label className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-[#7C1D3A] text-slate-500 text-[10px] text-center">
              📷<br />Add photo
              <input
                type="file"
                accept="image/png,image/jpeg"
                capture="environment"
                multiple
                className="hidden"
                data-testid="maint-photo-input"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach((f) => {
                    if (!/^image\/(png|jpe?g)$/.test(f.type)) return;
                    if (f.size > 4 * 1024 * 1024) { toast.error(`${f.name} is over 4MB — skipped`); return; }
                    const reader = new FileReader();
                    reader.onload = () => setPhotos((ps) => [...ps, String(reader.result)]);
                    reader.readAsDataURL(f);
                  });
                  e.target.value = "";
                }}
              />
            </label>
          </div>
        </div>
        {/* Consumables issued to this maintenance job (auto-deduct stock on completion) */}
        <div className="border-t border-slate-200 pt-3">
          <div className="flex items-center justify-between mb-2">
            <Label>Consumables used <span className="text-slate-400 text-[11px] font-normal">(stock auto-deducted when saved as Completed)</span></Label>
            <Button type="button" size="sm" variant="outline" onClick={addConsumable} data-testid="add-consumable-used-btn">
              + Add consumable
            </Button>
          </div>
          {consumablesUsed.length === 0 && (
            <div className="text-xs text-slate-500 italic">No consumables linked yet.</div>
          )}
          {consumablesUsed.map((r, idx) => {
            const master = consumablesMaster.find((c) => c.id === r.consumable_id);
            const stock = master ? Number(master.current_stock) || 0 : 0;
            const overUse = master && Number(r.qty) > stock;
            return (
              <div key={r.id} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-md p-2 mb-2" data-testid={`maint-consumable-${idx}`}>
                <select
                  value={r.consumable_id}
                  onChange={(e) => updateConsumable(r.id, "consumable_id", e.target.value)}
                  className="col-span-6 h-9 rounded-md border border-slate-200 px-2 text-sm"
                >
                  <option value="">— Select consumable —</option>
                  {consumablesMaster.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} · {c.name} (stock {c.current_stock} {c.unit_of_measure})
                    </option>
                  ))}
                </select>
                <Input
                  type="number" min="0" step="0.01"
                  value={r.qty}
                  onChange={(e) => updateConsumable(r.id, "qty", e.target.value)}
                  className={`col-span-2 text-right ${overUse ? "border-red-400" : ""}`}
                  placeholder="Qty"
                />
                <Input
                  value={r.note}
                  onChange={(e) => updateConsumable(r.id, "note", e.target.value)}
                  className="col-span-3"
                  placeholder="Note (optional)"
                />
                <button type="button" onClick={() => removeConsumable(r.id)} className="col-span-1 text-red-500 hover:text-red-700 text-xs">
                  Remove
                </button>
                {overUse && (
                  <div className="col-span-12 text-[11px] text-red-600">
                    Requested qty exceeds available stock ({stock} {master?.unit_of_measure}).
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button type="submit" disabled={submitting} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="save-maint">
            {submitting ? "Saving..." : "Save Entry"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
